import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react'
import { cn } from '@/lib/utils'

export interface PaginationProps {
  page: number
  pageSize: number
  totalItems: number
  pageSizeOptions?: number[]
  onPageChange: (page: number) => void
  onPageSizeChange: (size: number) => void
  /** List name: wraps the controls in a named navigation and makes button names unique per list. */
  label?: string
}

const DEFAULT_PAGE_SIZES = [25, 50, 100, 200, 300, 500]

function pageWindow(current: number, last: number): Array<number | '…'> {
  if (last <= 7) return Array.from({ length: last }, (_, i) => i + 1)
  const set = new Set<number>([1, last, current - 1, current, current + 1])
  set.add(2); set.add(last - 1)
  const sorted = [...set].filter((n) => n >= 1 && n <= last).sort((a, b) => a - b)
  const out: Array<number | '…'> = []
  for (let i = 0; i < sorted.length; i++) {
    out.push(sorted[i]!)
    const next = sorted[i + 1]
    if (next !== undefined && next > sorted[i]! + 1) out.push('…')
  }
  return out
}

export default function Pagination({
  page,
  pageSize,
  totalItems,
  pageSizeOptions = DEFAULT_PAGE_SIZES,
  onPageChange,
  onPageSizeChange,
  label,
}: PaginationProps) {
  const named = (name: string) => (label ? `${name}: ${label}` : name)
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize))
  const current = page + 1
  const windowNums = pageWindow(current, totalPages)
  const first = totalItems === 0 ? 0 : page * pageSize + 1
  const lastIdx = Math.min(totalItems, (page + 1) * pageSize)

  function go(target1Based: number) {
    const clamped = Math.max(1, Math.min(totalPages, target1Based))
    onPageChange(clamped - 1)
  }

  const Container = label ? 'nav' : 'div'
  return (
    <Container
      {...(label ? { 'aria-label': `Seitennavigation: ${label}` } : {})}
      className="flex flex-wrap items-center justify-between gap-3 text-sm text-[var(--muted-foreground)]"
    >
      <div className="flex items-center gap-3">
        <span>
          {first}–{lastIdx} von {totalItems.toLocaleString('de-DE')}
        </span>
        <label className="flex items-center gap-1">
          <span className="text-xs">Pro Seite:</span>
          <select
            {...(label ? { 'aria-label': named('Pro Seite') } : {})}
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            className="h-8 rounded-md border border-[var(--border)] bg-[var(--background)] px-2 text-sm"
          >
            {pageSizeOptions.map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </label>
      </div>
      <div className="flex items-center gap-1">
        <button
          onClick={() => go(1)}
          disabled={page === 0}
          className="rounded-md border border-[var(--border)] p-1.5 disabled:opacity-30"
          aria-label={named('Erste Seite')}
        >
          <ChevronsLeft className="h-4 w-4" />
        </button>
        <button
          onClick={() => go(current - 1)}
          disabled={page === 0}
          className="rounded-md border border-[var(--border)] p-1.5 disabled:opacity-30"
          aria-label={named('Vorherige Seite')}
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        {windowNums.map((n, i) =>
          n === '…' ? (
            <span key={`e${i}`} className="px-1.5 text-xs">…</span>
          ) : (
            <button
              key={n}
              onClick={() => go(n)}
              {...(label ? { 'aria-label': named(`Seite ${n}`) } : {})}
              aria-current={n === current ? 'page' : undefined}
              className={cn(
                'min-w-8 rounded-md border px-2 py-1 text-xs tabular-nums',
                n === current
                  ? 'border-[var(--primary)] bg-[var(--primary)] text-[var(--primary-foreground)]'
                  : 'border-[var(--border)] bg-[var(--background)] hover:bg-[var(--muted)]',
              )}
            >
              {n}
            </button>
          ),
        )}
        <button
          onClick={() => go(current + 1)}
          disabled={page >= totalPages - 1}
          className="rounded-md border border-[var(--border)] p-1.5 disabled:opacity-30"
          aria-label={named('Nächste Seite')}
        >
          <ChevronRight className="h-4 w-4" />
        </button>
        <button
          onClick={() => go(totalPages)}
          disabled={page >= totalPages - 1}
          className="rounded-md border border-[var(--border)] p-1.5 disabled:opacity-30"
          aria-label={named('Letzte Seite')}
        >
          <ChevronsRight className="h-4 w-4" />
        </button>
      </div>
    </Container>
  )
}
